import { Component } from "@theme/component";
import { DialogComponent } from "@theme/dialog";
import {
  CookieManager,
  getLocalStorage,
  setLocalStorage,
  hasLocalStorage,
} from "@theme/utilities";
import {
  ThemeEvents,
  CompareAddEvent,
  CompareRemoveEvent,
  CompareClearEvent,
  CompareToggleEvent,
} from "@theme/events";

const STORAGE_KEY = CookieManager.PRODUCT_COMPARE;
const MODE_STORAGE_KEY = CookieManager.PRODUCT_COMPARE_MODE;
const STORAGE_EXPIRY_DAYS = 30;
const BODY_CLASS = "is-product-comparing";

function getConfig() {
  return window.MorgennTheme?.compare ?? {};
}

function getMaxItems() {
  return getConfig().maxItems ?? 4;
}

function t(key, fallback) {
  return getConfig().translations?.[key] ?? fallback;
}

export const CompareUtils = {
  getList() {
    if (!hasLocalStorage()) return [];
    return getLocalStorage(STORAGE_KEY) ?? [];
  },

  has(productId) {
    const id = String(productId);
    return this.getList().some((entry) => entry.id === id);
  },

  count() {
    return this.getList().length;
  },

  add(productId, productUrl) {
    if (!hasLocalStorage()) return false;
    const id = String(productId);
    const list = this.getList();
    if (list.some((entry) => entry.id === id)) return true;

    if (list.length >= getMaxItems()) {
      const msg = t("maxReached", `You can compare up to ${getMaxItems()} products at a time.`);
      window.alert(msg);
      return false;
    }

    list.push({ id, u: productUrl });
    setLocalStorage(STORAGE_KEY, list, STORAGE_EXPIRY_DAYS);
    document.dispatchEvent(new CompareAddEvent(id, productUrl));
    return true;
  },

  remove(productId) {
    if (!hasLocalStorage()) return;
    const id = String(productId);
    const next = this.getList().filter((entry) => entry.id !== id);
    setLocalStorage(STORAGE_KEY, next, STORAGE_EXPIRY_DAYS);
    document.dispatchEvent(new CompareRemoveEvent(id));
  },

  clear() {
    if (!hasLocalStorage()) return;
    setLocalStorage(STORAGE_KEY, [], STORAGE_EXPIRY_DAYS);
    document.dispatchEvent(new CompareClearEvent());
  },

  syncCheckboxes(root = document) {
    const list = this.getList();
    const ids = new Set(list.map((entry) => entry.id));
    root.querySelectorAll(".js-compare-checkbox").forEach((input) => {
      const id = input.dataset.productId;
      const wasChecked = input.checked;
      const shouldBeChecked = ids.has(String(id));
      if (wasChecked !== shouldBeChecked) input.checked = shouldBeChecked;
    });
  },

  isModeActive() {
    if (!hasLocalStorage()) return false;
    return getLocalStorage(MODE_STORAGE_KEY) === true;
  },

  setModeActive(active) {
    if (!hasLocalStorage()) return;
    setLocalStorage(MODE_STORAGE_KEY, !!active, STORAGE_EXPIRY_DAYS);
  },
};

if (typeof window !== "undefined") {
  window.CompareUtils = CompareUtils;
}

class CompareSwitchComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#input = this.querySelector('input[type="checkbox"]');
    if (!this.#input) return;
    this.#input.checked = CompareUtils.isModeActive();
    this.#applyBodyState(this.#input.checked);
    this.#input.addEventListener("change", this.#handleChange);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener("change", this.#handleChange);
    super.disconnectedCallback?.();
  }

  #input = null;

  #handleChange = () => {
    const active = !!this.#input?.checked;
    CompareUtils.setModeActive(active);
    this.#applyBodyState(active);
    document.dispatchEvent(new CompareToggleEvent(active));
  };

  #applyBodyState(active) {
    document.body.classList.toggle(BODY_CLASS, active);
  }
}

if (!customElements.get("compare-switch")) {
  customElements.define("compare-switch", CompareSwitchComponent);
}

class CompareCheckboxComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#input = this.querySelector("input.js-compare-checkbox");
    if (!this.#input) return;
    this.#input.checked = CompareUtils.has(this.#input.dataset.productId);
    this.#input.addEventListener("change", this.#handleChange);
    document.addEventListener(ThemeEvents.compareRemove, this.#onRemoved);
    document.addEventListener(ThemeEvents.compareClear, this.#onCleared);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener("change", this.#handleChange);
    document.removeEventListener(ThemeEvents.compareRemove, this.#onRemoved);
    document.removeEventListener(ThemeEvents.compareClear, this.#onCleared);
    super.disconnectedCallback?.();
  }

  #input = null;

  #handleChange = () => {
    const id = this.#input.dataset.productId;
    const url = this.#input.dataset.productUrl;
    if (this.#input.checked) {
      const ok = CompareUtils.add(id, url);
      if (!ok) this.#input.checked = false;
    } else {
      CompareUtils.remove(id);
    }
  };

  #onRemoved = (event) => {
    const id = event.detail?.productId;
    if (id && String(id) === String(this.#input?.dataset.productId)) {
      if (this.#input) this.#input.checked = false;
    }
  };

  #onCleared = () => {
    if (this.#input) this.#input.checked = false;
  };
}

if (!customElements.get("compare-checkbox")) {
  customElements.define("compare-checkbox", CompareCheckboxComponent);
}

class CompareBarComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#itemsList = this.querySelector('[ref="items"]');
    this.#countLabel = this.querySelector('[ref="count"]');
    this.#openButton = this.querySelector('[ref="openButton"]');
    this.#clearButton = this.querySelector('[ref="clearButton"]');

    document.addEventListener(ThemeEvents.compareAdd, this.#onAdd);
    document.addEventListener(ThemeEvents.compareRemove, this.#onRemove);
    document.addEventListener(ThemeEvents.compareClear, this.#onClear);

    this.#clearButton?.addEventListener("click", this.#handleClearClick);

    this.#hydrate();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.compareAdd, this.#onAdd);
    document.removeEventListener(ThemeEvents.compareRemove, this.#onRemove);
    document.removeEventListener(ThemeEvents.compareClear, this.#onClear);
    this.#clearButton?.removeEventListener("click", this.#handleClearClick);
    super.disconnectedCallback?.();
  }

  #itemsList = null;
  #countLabel = null;
  #openButton = null;
  #clearButton = null;

  #handleClearClick = (event) => {
    event.preventDefault();
    CompareUtils.clear();
  };

  #hydrate() {
    const list = CompareUtils.getList();
    if (!list.length) {
      this.#updateState();
      return;
    }
    Promise.all(list.map((entry) => this.#fetchItem(entry.id, entry.u)))
      .then(() => this.#updateState())
      .catch(() => this.#updateState());
  }

  #onAdd = (event) => {
    const { productId, productUrl } = event.detail || {};
    if (!productId) return;
    this.#fetchItem(productId, productUrl).finally(() => this.#updateState());
  };

  #onRemove = (event) => {
    const id = event.detail?.productId;
    if (!id) return;
    const el = this.#itemsList?.querySelector(`[data-product-id="${CSS.escape(String(id))}"]`);
    el?.remove();
    this.#updateState();
  };

  #onClear = () => {
    if (this.#itemsList) this.#itemsList.innerHTML = "";
    this.#updateState();
  };

  async #fetchItem(productId, productUrl) {
    if (!this.#itemsList || !productUrl) return;
    if (this.#itemsList.querySelector(`[data-product-id="${CSS.escape(String(productId))}"]`)) return;

    try {
      const sectionId = this.dataset.itemSectionId || "product-compare-bar-item";
      const url = `${productUrl}?section_id=${encodeURIComponent(sectionId)}`;
      const response = await fetch(url);
      if (!response.ok) return;
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const fragment = doc.querySelector('[data-compare-bar-item="true"]');
      if (fragment) {
        this.#itemsList.appendChild(fragment);
      }
    } catch (err) {
      console.warn("[compare] failed to fetch bar item", err);
    }
  }

  #updateState() {
    const count = CompareUtils.count();
    const visible = count > 0;
    this.classList.toggle("is-visible", visible);
    document.body.classList.toggle("compare-bar-active", visible);

    if (this.#countLabel) {
      const oneTpl = t("barCountOne", "__COUNT__ product selected");
      const otherTpl = t("barCountOther", "__COUNT__ products selected");
      const template = count === 1 ? oneTpl : otherTpl;
      this.#countLabel.textContent = template.replace("__COUNT__", count);
    }

    if (this.#openButton) {
      this.#openButton.disabled = count < 2;
      this.#openButton.setAttribute("aria-disabled", String(count < 2));
    }
  }
}

if (!customElements.get("compare-bar")) {
  customElements.define("compare-bar", CompareBarComponent);
}

class CompareDrawerComponent extends DialogComponent {
  requiredRefs = ["dialog"];

  connectedCallback() {
    super.connectedCallback();
    this.#tableBody = this.querySelector('[ref="tableBody"]');
    this.#columnTemplate = this.querySelector('template[ref="columnTemplate"]');
    this.#emptyState = this.querySelector('[ref="empty"]');

    document.addEventListener(ThemeEvents.compareRemove, this.#onListChanged);
    document.addEventListener(ThemeEvents.compareClear, this.#onListChanged);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.compareRemove, this.#onListChanged);
    document.removeEventListener(ThemeEvents.compareClear, this.#onListChanged);
    super.disconnectedCallback();
  }

  open = (event) => {
    event?.preventDefault?.();
    if (CompareUtils.count() < 2) return;
    this.#renderTable().finally(() => this.showDialog());
  };

  close = () => {
    this.closeDialog();
  };

  clear = (event) => {
    event?.preventDefault?.();
    CompareUtils.clear();
  };

  #tableBody = null;
  #columnTemplate = null;
  #emptyState = null;

  #onListChanged = () => {
    if (!this.refs.dialog?.open) return;
    if (CompareUtils.count() < 2) {
      this.closeDialog();
      return;
    }
    this.#renderTable();
  };

  async #renderTable() {
    if (!this.#tableBody) return;

    const products = CompareUtils.getList();
    const sectionId = this.dataset.sectionId;

    this.#clearColumns();

    if (!products.length || !sectionId) {
      this.#emptyState?.removeAttribute("hidden");
      return;
    }
    this.#emptyState?.setAttribute("hidden", "");

    try {
      const fragments = await Promise.all(
        products.map((entry) => this.#fetchProductFragments(entry.u, sectionId, entry.id))
      );
      for (const productFragments of fragments) {
        if (!productFragments) continue;
        this.#appendColumn(productFragments);
      }
      this.#pruneEmptyRows();

      // Size the mobile CSS grid: 1 sticky label column + one column per product.
      // Written out explicitly so it never depends on repeat(var()) support.
      const firstRow = this.#tableBody.querySelector("tr[data-compare-key]");
      const columnCount = firstRow ? firstRow.querySelectorAll("td.compare-drawer__cell").length : 0;
      const table = this.#tableBody.closest("table");
      if (table) {
        const cols = columnCount || 1;
        table.style.setProperty("--compare-count", String(cols));
        table.style.gridTemplateColumns = `9rem repeat(${cols}, clamp(13rem, 38vw, 20rem))`;
      }
    } catch (err) {
      console.warn("[compare] failed to render table", err);
    }
  }

  async #fetchProductFragments(productUrl, sectionId, productId) {
    if (!productUrl) return null;
    try {
      const response = await fetch(`${productUrl}?section_id=${encodeURIComponent(sectionId)}`);
      if (!response.ok) return null;
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const template = doc.querySelector('template[data-compare-rows="true"]');
      if (!template) return null;
      return { template: template.content, productId };
    } catch (err) {
      console.warn("[compare] product fetch failed", err);
      return null;
    }
  }

  #appendColumn({ template, productId }) {
    if (!this.#tableBody) return;

    const rows = this.#tableBody.querySelectorAll("tr[data-compare-key]");
    rows.forEach((row) => {
      const key = row.getAttribute("data-compare-key");
      const cellSource = template.querySelector(`[data-compare-cell="${key}"]`);
      const td = document.createElement("td");
      td.className = "compare-drawer__cell";
      td.dataset.productId = productId;
      if (cellSource) {
        td.appendChild(cellSource.cloneNode(true));
      } else {
        td.innerHTML = `<span class="compare-drawer__no-data">${t("noData", "—")}</span>`;
      }
      row.appendChild(td);
    });
  }

  #clearColumns() {
    if (!this.#tableBody) return;
    this.#tableBody.querySelectorAll("tr[data-compare-key]").forEach((row) => {
      row.querySelectorAll("td.compare-drawer__cell").forEach((cell) => cell.remove());
    });
  }

  #pruneEmptyRows() {
    if (!this.#tableBody) return;
    const ALWAYS_VISIBLE_ROWS = new Set([
      "info",
      "price",
    ]);
    this.#tableBody.querySelectorAll("tr[data-compare-key]").forEach((row) => {
      const key = row.getAttribute("data-compare-key");
      if (ALWAYS_VISIBLE_ROWS.has(key)) {
        row.removeAttribute("hidden");
        return;
      }
      const cells = row.querySelectorAll("td.compare-drawer__cell");
      const allHaveValue = cells.length > 0 && Array.from(cells).every((cell) => {
        const inner = cell.querySelector("[data-compare-has-value]");
        return inner ? inner.dataset.compareHasValue === "true" : false;
      });
      row.toggleAttribute("hidden", !allHaveValue);
    });
  }
}

if (!customElements.get("compare-drawer")) {
  customElements.define("compare-drawer", CompareDrawerComponent);
}

class CompareRemoveButton extends HTMLButtonElement {
  connectedCallback() {
    this.addEventListener("click", this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#handleClick);
  }

  #handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const id = this.dataset.productId;
    if (id) CompareUtils.remove(id);
  };
}

if (!customElements.get("compare-remove")) {
  customElements.define("compare-remove", CompareRemoveButton, { extends: "button" });
}

document.addEventListener(ThemeEvents.FilterUpdate, () => {
  requestAnimationFrame(() => CompareUtils.syncCheckboxes());
});

document.addEventListener(ThemeEvents.pageLoaded, () => {
  if (CompareUtils.isModeActive()) document.body.classList.add(BODY_CLASS);
  CompareUtils.syncCheckboxes();
});

if (CompareUtils.isModeActive()) {
  document.body.classList.add(BODY_CLASS);
}
